// @givmo/board — Settings → Board → "Calendar": Board's calendar settings in one place (CBG-UX14).
// The simple card Jonathan approved on 2026-11-01 19:24 PST (D-CBG-56), which supersedes the drawn
// "Calendar invitations" card; its approved renders are the reference
// (`scratchpad/2026-11-01-ux14-calendar-settings-design/simple-approved/`).
//
// One main line and one act, chosen by what sends the reader's own meetings. By D-CBG-54 that is the
// board account whenever one is connected, else the reader's own calendar:
//   * nothing sends them: "Connect a Google Calendar, …" with Connect (the connect dialog, which also
//     offers an Owner the board account), and what happens until then: Givmo emails the invitations,
//     or, while Givmo's email is off, each person adds meetings to their own calendar from Board.
//     Where Google isn't offered nothing can be connected, so the card stays away instead;
//   * a calendar sends them: "Invitations are sent from {address}." with Disconnect for whoever may
//     disconnect it (the board account is an Owner's; one's own calendar is one's own), and one small
//     line where it applies. Beside the board account that is the reader's own calendar, as a choice
//     when scheduling, or a link to connect or reconnect it (the schedule dialog doesn't offer that
//     while a board account sends). Under an Owner's own calendar it is a link to a board account;
//   * that calendar needs reconnecting: the line in amber with Reconnect for whoever may (a reader
//     without the Owner string is told an Owner needs to), who connected the board account (Q4), and
//     what happens until it's reconnected. One's own calendar Board stopped using when one's access
//     ended (`organizer_left`) says that instead of Google's refusal (D-CBG-56's extension).
// Then, for an Owner and only when it happens, an officer who left with upcoming meetings still going
// out from their calendar, and Move. Then, while a Google calendar sends the reader's meetings,
// "Invitation options": the two guest settings as one sentence, and Change for an Owner (the
// invitation's title is chosen inside the Change dialog).
//
// It reads:
//   * Board's senders read (`board:read`): what Board sends from while nothing of the reader's does,
//     and whether Google is offered;
//   * core's connections read: the reader's own calendar, and the board account with who connected it
//     (Q4: `connected_by_*`, which Board's own connections read doesn't carry). Core lists the board
//     account to everyone holding the card's own string (`_can_see`, org_integration_service.py);
//   * Board's connections read (`board_calendar:manage`): an Owner's view of every calendar and the
//     meetings each sends, for the board account's Disconnect and an officer who left;
//   * Board's settings read (`board:read`), and the meetings list for the days.
//
// ⚠ NOTHING AN OWNER DOES IS DRAWN FOR ANYONE ELSE: the board account's acts, Move, Change and Apply
// are ABSENT without `board_calendar:manage`, never disabled (Jonathan, 2026-09-28).
//
// DARK until served: the settings read and Board's connections read are the connected channel's
// (`CONNECTED`), so while `BOARD_CALENDAR_INVITATIONS_ENABLED` is off they answer the route miss and
// the card renders nothing. It is Board's one settings panel, so the Settings composer then hides
// Board's whole section, heading included, rather than leave "Board settings" over nothing.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  BoardCalendarConnectionOut,
  BoardCalendarSendersOut,
  BoardCalendarSettingsOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Switch,
  toast,
} from '@givmo/design-system';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { HowThisWorks } from '../../components/fine-print';
import { CALENDAR_PROVIDER } from '../../calendar/calendar-connect';
import {
  isUpcomingScheduled,
  meetingsNamed,
  meetingsSentFrom,
  soonestFirst,
  useMeetingsById,
  useOfficerDirectory,
} from '../../calendar/calendar-model';
import {
  useBoardCalendarConnections,
  useBoardCalendarSettings,
  useCalendarSenders,
  useOrgConnections,
  useRefreshCalendar,
  type CalendarConnection,
} from '../../calendar/calendar-queries';
import { calendarWords as w } from '../../calendar/calendar-words';
import { useCalendarConnect } from '../../calendar/use-calendar-connect';
import { DisconnectDialog, MoveMeetingsDialog, failureLine } from './account-dialogs';
import {
  BODY,
  DIALOG_TITLE,
  SMALL,
  STRONG,
  TITLE,
  instantDay,
  organizationDomain,
} from './calendar-parts';
import { ConnectAnswer, ConnectDialog } from './connect-dialog';

/** The one Board Owner string the calendar adds (`board-calendar.md` §3). */
export const CALENDAR_MANAGE_PERMISSION = 'board_calendar:manage';
/** The string scheduling a meeting, and connecting one's own calendar, rides: the card's gate. */
export const CALENDAR_SCHEDULE_PERMISSION = 'board_meeting:write';

type Row = BoardCalendarConnectionOut;
type SendFrom = 'my_calendar' | 'board_account' | 'givmo_email';

type Open =
  | { kind: 'connect'; mode: 'mine' | 'board-account'; fromRow: boolean }
  | { kind: 'move'; row: Row }
  | { kind: 'disconnect'; row: Row }
  | { kind: 'disconnect-mine' }
  | null;

/** The card's main lines: the body's size, a step stronger. */
const MAIN = 'font-sans text-s14 font-w500 leading-h15 tracking-givmo text-text-strong';

interface Values {
  guests_can_modify: boolean;
  guest_list_visible: boolean;
  titles_in_invitations: boolean;
}

function valuesOf(settings: BoardCalendarSettingsOut): Values {
  return {
    guests_can_modify: settings.guests_can_modify,
    guest_list_visible: settings.guest_list_visible,
    titles_in_invitations: settings.titles_in_invitations,
  };
}

/** The person's own live personal connection, if they have one (core lists every reader their own). */
function ownConnection(
  rows: readonly CalendarConnection[],
  userId: string | null,
): CalendarConnection | undefined {
  if (userId === null) return undefined;
  return rows.find(
    (row) =>
      row.account_kind === 'personal' &&
      row.owner_user_id === userId &&
      row.status !== 'disconnected',
  );
}

function asSendFrom(value: string | null | undefined): SendFrom | null {
  return value === 'my_calendar' || value === 'board_account' || value === 'givmo_email'
    ? value
    : null;
}

/**
 * The two guest settings as one sentence. Three, not four: guests can change a meeting only while
 * they can see who's invited (Google's own editor forces it, and the backend refuses the pair).
 */
function optionsLine(settings: BoardCalendarSettingsOut): string {
  if (settings.guests_can_modify) return w.optionsBoth;
  return settings.guest_list_visible ? w.optionsSeeOnly : w.optionsNeither;
}

export function BoardCalendarCard({
  onDirtyChange,
}: {
  /** The settings page's unsaved-changes guard, while the settings dialog holds a change. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { orgRef, permissions, userId, userEmail, orgName } = useBoardHost();
  const operations = useBoardOperations();
  const canSchedule = hasBoardPermission(permissions, CALENDAR_SCHEDULE_PERMISSION);
  const canManage = hasBoardPermission(permissions, CALENDAR_MANAGE_PERMISSION);
  const senders = useCalendarSenders(orgRef, canSchedule);
  const core = useOrgConnections(orgRef, canSchedule);
  const settings = useBoardCalendarSettings(orgRef, canSchedule);
  const board = useBoardCalendarConnections(orgRef, canSchedule && canManage);
  const served =
    senders.data != null &&
    core.data != null &&
    settings.data != null &&
    (!canManage || board.data != null);
  // Other officers' names (an Owner's view only): the organization's team list.
  const directory = useOfficerDirectory(orgRef, served && canManage);
  const meetings = useMeetingsById(orgRef, served);
  const refresh = useRefreshCalendar(orgRef);
  const flow = useCalendarConnect(orgRef);
  const [open, setOpen] = useState<Open>(null);
  /** Which calendar a Reconnect went to Google for, so its answer opens titled right. */
  const [reconnecting, setReconnecting] = useState<'board' | 'mine'>('mine');
  const [editing, setEditing] = useState(false);
  /** After a save in this visit: the upcoming meetings that still carry the earlier settings. */
  const [applyCount, setApplyCount] = useState<number | null>(null);

  const ownerRows = useMemo(
    () => (board.data ?? []).filter((row) => row.status !== 'disconnected'),
    [board.data],
  );
  const upcomingCount = useMemo(() => {
    const ids = new Set<string>();
    for (const row of ownerRows) {
      for (const id of row.upcoming_meeting_ids ?? []) ids.add(String(id));
    }
    return ids.size;
  }, [ownerRows]);
  const exampleTitle = useMemo(
    () =>
      soonestFirst([...meetings.byId.values()].filter((meeting) => isUpcomingScheduled(meeting)))[0]
        ?.title ?? null,
    [meetings.byId],
  );

  const apply = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerApplyBoardCalendarSettings({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      const applied = data?.applied ?? 0;
      const attention = data?.needs_attention ?? 0;
      // A partial apply is not a success: the warning's own mark, never a check.
      if (attention > 0) toast.warning(w.appliedPartly(applied, attention));
      else toast.success(w.applied(applied));
      setApplyCount(null);
      await refresh();
    },
    onError: (error) => toast.error(failureLine(error)),
  });

  if (!canSchedule || !served || !meetings.loaded || orgRef === null) return null;
  const sendersData = senders.data as BoardCalendarSendersOut;
  const current = settings.data as BoardCalendarSettingsOut;
  const coreRows = core.data ?? [];
  const name = orgName ?? '';
  const googleOffered = (sendersData.offered_providers ?? []).includes(CALENDAR_PROVIDER);
  const mine = ownConnection(coreRows, userId ?? null);
  // The board account as core lists it to every reader of this card; Board's own row (an Owner's)
  // carries the meetings it sends, for its Disconnect.
  const boardCore = coreRows.find(
    (row) => row.account_kind === 'board_account' && row.status !== 'disconnected',
  );
  const boardRow = ownerRows.find((row) => row.account_kind === 'board_account');
  // What Board sends the reader's meetings from while no calendar of theirs can.
  const resolved = asSendFrom(sendersData.default_send_from);
  const ownEmail = userEmail ?? (userId ? directory.emailById.get(userId) : undefined) ?? null;
  const domain = organizationDomain([
    ownEmail,
    mine?.account_email,
    ...ownerRows.map((row) => row.account_email),
  ]);
  const meetingsById = meetings.byId;
  const ownerNameOf = (row: Row) =>
    (row.owner_user_id ? directory.byId.get(row.owner_user_id) : undefined) ?? null;

  // What sends the reader's own meetings (D-CBG-54), whether it can, and who may act on it.
  const sending =
    boardCore !== undefined
      ? { kind: 'board' as const, row: boardCore }
      : mine !== undefined
        ? { kind: 'mine' as const, row: mine }
        : null;
  // Nothing sends the reader's meetings and nothing can be connected here (Google isn't offered in
  // this environment): no line is true and no act can be offered, so the card stays away, as Your
  // settings' card always has, and the composer then hides Board's section with it.
  if (sending === null && !googleOffered) return null;
  const broken = sending !== null && sending.row.status !== 'connected';
  const mayAct = sending !== null && (sending.kind === 'mine' || canManage);
  const connectedBy = (() => {
    if (boardCore === undefined || !boardCore.connected_at) return null;
    const day = instantDay(boardCore.connected_at);
    if (boardCore.connected_by_user_id && boardCore.connected_by_user_id === userId) {
      return w.connectedByYou(day);
    }
    const who = boardCore.connected_by_display_name?.trim();
    return who ? w.connectedBy(who, day) : null;
  })();
  const openConnect = (mode: 'mine' | 'board-account', fromRow: boolean) => {
    flow.reset();
    setOpen({ kind: 'connect', mode, fromRow });
  };
  const reconnect = (
    target: 'board' | 'mine',
    row: { connection_id: string; account_email: string },
  ) => {
    setReconnecting(target);
    flow.begin({
      accountKind: target === 'board' ? 'board_account' : 'personal',
      reconnectConnectionId: row.connection_id,
      loginHint: row.account_email,
    });
  };
  const link = (label: string, onClick: () => void, testId: string) => (
    <Button appearance="saas" tone="link" size="sm" onClick={onClick} testId={testId}>
      {label}
    </Button>
  );

  let line: ReactNode;
  let act: ReactNode = null;
  const small: ReactNode[] = [];
  if (sending === null) {
    // Google is offered here (the card stays away otherwise, above).
    line = w.connectToSend;
    act = (
      <Button
        appearance="saas"
        tone="secondary"
        size="sm"
        onClick={() => openConnect('mine', false)}
        testId="board-calendar-connect"
      >
        {w.connect}
      </Button>
    );
    if (resolved === 'givmo_email') small.push(w.untilGivmo);
    else if (resolved === null) small.push(w.untilNobody);
  } else if (!broken) {
    line = w.sentFromAddress(sending.row.account_email);
    if (mayAct) {
      act = (
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={() =>
            sending.kind === 'mine'
              ? setOpen({ kind: 'disconnect-mine' })
              : boardRow !== undefined && setOpen({ kind: 'disconnect', row: boardRow })
          }
          testId="board-calendar-disconnect"
        >
          {w.disconnect}
        </Button>
      );
    }
    if (sending.kind === 'board') {
      if (mine !== undefined && mine.status === 'connected') {
        small.push(
          <>
            {w.ownAlsoChoosable(mine.account_email)}{' '}
            {link(
              w.disconnect,
              () => setOpen({ kind: 'disconnect-mine' }),
              'board-calendar-disconnect-mine',
            )}
          </>,
        );
      } else if (mine !== undefined && googleOffered) {
        small.push(
          link(w.reconnectInline, () => reconnect('mine', mine), 'board-calendar-reconnect-mine'),
        );
      } else if (googleOffered) {
        small.push(
          link(w.connectOwnLink, () => openConnect('mine', true), 'board-calendar-connect-mine'),
        );
      }
    } else if (canManage && googleOffered) {
      small.push(
        link(
          w.useBoardAccountLink,
          () => openConnect('board-account', true),
          'board-calendar-use-board',
        ),
      );
    }
  } else {
    // One's own calendar left `organizer_left` when one's access to Board ended (core keeps it so
    // after access returns, until a reconnect): Board stopped, not Google. Every other status is
    // Google's refusal.
    line = (
      <span className="text-warning-700">
        {sending.kind === 'mine' && sending.row.status === 'organizer_left'
          ? w.accessEnded(sending.row.account_email)
          : w.stoppedUsing(sending.row.account_email)}
      </span>
    );
    if (mayAct && googleOffered) {
      act = (
        <Button
          appearance="saas"
          tone="primary"
          size="sm"
          loading={flow.starting}
          onClick={() => reconnect(sending.kind, sending.row)}
          testId="board-calendar-reconnect"
        >
          {w.reconnect}
        </Button>
      );
    } else if (sending.kind === 'board' && !canManage) {
      small.push(w.ownerMustReconnect);
    }
    if (sending.kind === 'board' && connectedBy !== null) small.push(connectedBy);
    if (resolved === 'givmo_email') small.push(w.untilReconnectedGivmo);
    else if (resolved === null) small.push(w.untilReconnectedNobody);
  }

  // Only for an Owner, and only when it happens: an officer who left whose calendar still sends
  // upcoming meetings.
  const moves = ownerRows
    .filter(
      (row) =>
        canManage &&
        row.account_kind === 'personal' &&
        row.owner_user_id !== userId &&
        row.status === 'organizer_left' &&
        (row.upcoming_meeting_ids ?? []).length > 0,
    )
    .map((row) => ({ row, label: ownerNameOf(row) }))
    .sort((left, right) =>
      (left.label ?? left.row.account_email).localeCompare(right.label ?? right.row.account_email),
    );
  const showsOptions = sending !== null && sending.row.provider === CALENDAR_PROVIDER;

  return (
    <Card testId="board-calendar">
      <CardHeader className="flex-col sm:flex-row">
        <h3 className={TITLE}>{w.boardTitle}</h3>
      </CardHeader>

      <div
        className="flex flex-col gap-12 px-20 py-16 sm:flex-row sm:items-start sm:justify-between sm:gap-16"
        data-testid="board-calendar-main"
      >
        <div className="flex min-w-0 flex-col gap-4">
          <p className={MAIN}>{line}</p>
          {small.map((part, index) => (
            <p key={index} className={SMALL}>
              {part}
            </p>
          ))}
        </div>
        {act === null ? null : <div className="flex shrink-0 items-center gap-8">{act}</div>}
      </div>

      {moves.map(({ row, label }) => {
        const count = (row.upcoming_meeting_ids ?? []).length;
        return (
          <div
            key={row.connection_id}
            className="flex flex-col gap-12 border-t border-border-subtle px-20 py-16 sm:flex-row sm:items-center sm:justify-between sm:gap-16"
            data-testid={`board-calendar-left-${row.connection_id}`}
          >
            <p className={MAIN}>{w.leftWithMeetings(label, count, row.account_email)}</p>
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              onClick={() => setOpen({ kind: 'move', row })}
              testId={`board-calendar-${row.connection_id}-move`}
            >
              {count === 1 ? w.moveIt : w.moveThem}
            </Button>
          </div>
        );
      })}

      {showsOptions ? (
        <div
          className="flex flex-col gap-12 border-t border-border-subtle px-20 py-16 sm:flex-row sm:items-center sm:justify-between sm:gap-16"
          data-testid="board-calendar-options"
        >
          <div className="flex min-w-0 flex-col gap-4">
            <span className={STRONG}>{w.invitationOptions}</span>
            <span className={BODY}>{optionsLine(current)}</span>
            {canManage ? null : <span className={SMALL}>{w.ownersChange}</span>}
          </div>
          {canManage ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              testId="board-calendar-settings-change"
            >
              {w.change}
            </Button>
          ) : null}
        </div>
      ) : null}

      {canManage && applyCount !== null && applyCount > 0 ? (
        <div
          className="flex flex-col gap-8 border-t border-border-subtle bg-surface px-20 py-14 sm:flex-row sm:items-center sm:justify-between sm:gap-16"
          data-testid="board-calendar-settings-apply"
        >
          <div className="flex min-w-0 flex-col gap-2">
            <span className={STRONG}>{w.applyLine(applyCount)}</span>
            <span className={SMALL}>{w.applyNote}</span>
          </div>
          <Button
            appearance="saas"
            tone="primary"
            size="sm"
            loading={apply.isPending}
            onClick={() => apply.mutate()}
            testId="board-calendar-settings-apply-button"
          >
            {w.applyButton}
          </Button>
        </div>
      ) : null}

      <CardFooter className="py-12">
        <HowThisWorks testId="board-calendar-how" lines={w.boardHow} />
      </CardFooter>

      {editing && canManage ? (
        <SettingsDialog
          orgRef={orgRef}
          current={current}
          orgName={name}
          exampleTitle={exampleTitle}
          onDirtyChange={onDirtyChange}
          onSaved={async () => {
            setEditing(false);
            setApplyCount(upcomingCount);
            await refresh();
          }}
          onClose={() => setEditing(false)}
        />
      ) : null}
      {open?.kind === 'connect' ? (
        <ConnectDialog
          mode={open.mode}
          fromRow={open.fromRow}
          canManage={canManage}
          boardAccountExists={boardCore !== undefined}
          exampleDomain={domain}
          orgName={name}
          flow={flow}
          onClose={() => {
            flow.reset();
            setOpen(null);
          }}
        />
      ) : null}
      {/* A reconnect goes straight to Google; its answer opens here, as the connect dialog's would. */}
      {open === null && flow.outcome !== null ? (
        <Dialog.Root open onOpenChange={(next) => !next && flow.reset()}>
          <Dialog.Content
            aria-label={reconnecting === 'board' ? w.connectBoardTitle : w.connectTitle}
            width={448}
            className="flex flex-col gap-16"
            testId="calendar-connect-dialog"
          >
            <ConnectAnswer
              outcome={flow.outcome}
              boardAccount={reconnecting === 'board'}
              onClose={flow.reset}
              onAgain={flow.reset}
            />
          </Dialog.Content>
        </Dialog.Root>
      ) : null}
      {open?.kind === 'move' ? (
        <MoveMeetingsDialog
          orgRef={orgRef}
          account={open.row}
          ownerName={ownerNameOf(open.row)}
          meetings={meetingsNamed((open.row.upcoming_meeting_ids ?? []).map(String), meetingsById)}
          senders={sendersData}
          orgName={name}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'disconnect' ? (
        <DisconnectDialog
          orgRef={orgRef}
          account={open.row}
          own={open.row.owner_user_id != null && open.row.owner_user_id === userId}
          ownerName={
            open.row.account_kind === 'board_account'
              ? null
              : (ownerNameOf(open.row) ?? open.row.account_email)
          }
          meetings={meetingsNamed((open.row.upcoming_meeting_ids ?? []).map(String), meetingsById)}
          meetingCount={(open.row.upcoming_meeting_ids ?? []).length}
          senders={sendersData}
          orgName={name}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === 'disconnect-mine' && mine !== undefined ? (
        <DisconnectDialog
          orgRef={orgRef}
          account={mine}
          own
          ownerName={null}
          meetings={meetingsSentFrom(mine.account_email, meetingsById.values())}
          senders={sendersData}
          orgName={name}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Card>
  );
}

function SwitchRow({
  label,
  description,
  note,
  checked,
  onCheckedChange,
  testId,
}: {
  label: string;
  description?: string;
  note?: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  testId: string;
}) {
  return (
    <div className="flex items-start justify-between gap-16 py-12">
      <div className="flex min-w-0 flex-col gap-2">
        <span className={STRONG}>{label}</span>
        {description !== undefined ? <span className={SMALL}>{description}</span> : null}
        {note !== undefined ? (
          <span
            className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid={`${testId}-note`}
          >
            {note}
          </span>
        ) : null}
      </div>
      <Switch
        appearance="saas"
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={label}
        testId={testId}
        className="mt-2 shrink-0"
      />
    </div>
  );
}

// ⚠ GUEST EDITING NEEDS A VISIBLE GUEST LIST (Google's own editor forces it; the backend's PUT refuses
// the pair with a 422): turning the list off turns editing off, and turning editing on turns the list
// on. Both switches stay usable, and the line under the list says why. Saved settings apply to
// meetings scheduled after them; "Apply to them" then patches the upcoming meetings without mailing
// anyone (`POST …/calendar/settings/application`, `send_updates: none`).
function SettingsDialog({
  orgRef,
  current,
  orgName,
  exampleTitle,
  onDirtyChange,
  onSaved,
  onClose,
}: {
  orgRef: string;
  current: BoardCalendarSettingsOut;
  orgName: string;
  exampleTitle: string | null;
  onDirtyChange?: (dirty: boolean) => void;
  onSaved: () => Promise<void>;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const initial = valuesOf(current);
  const [values, setValues] = useState<Values>(initial);
  const dirty =
    values.guests_can_modify !== initial.guests_can_modify ||
    values.guest_list_visible !== initial.guest_list_visible ||
    values.titles_in_invitations !== initial.titles_in_invitations;
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerAdoptBoardCalendarSettings({
        path: { org_ref: orgRef },
        body: { ...values, new_time_proposals: current.new_time_proposals },
        throwOnError: true,
      });
    },
    onSuccess: onSaved,
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={w.invitationsTitle}
        width={520}
        className="flex flex-col gap-12"
        testId="board-calendar-settings-dialog"
      >
        <Dialog.Title className={DIALOG_TITLE}>{w.invitationsTitle}</Dialog.Title>

        <div className="flex flex-col divide-y divide-border-subtle">
          <SwitchRow
            label={w.modifyLabel}
            description={w.modifyDescription}
            checked={values.guests_can_modify}
            // Editing needs the list: turning editing on shows the list too.
            onCheckedChange={(next) =>
              setValues((prior) => ({
                ...prior,
                guests_can_modify: next,
                guest_list_visible: next ? true : prior.guest_list_visible,
              }))
            }
            testId="board-calendar-settings-modify"
          />
          <SwitchRow
            label={w.listLabel}
            description={values.guest_list_visible ? w.listDescription : undefined}
            note={values.guest_list_visible ? undefined : w.listOffNote}
            checked={values.guest_list_visible}
            // And the list off turns editing off with it.
            onCheckedChange={(next) =>
              setValues((prior) => ({
                ...prior,
                guest_list_visible: next,
                guests_can_modify: next ? prior.guests_can_modify : false,
              }))
            }
            testId="board-calendar-settings-list"
          />
        </div>

        <div className="flex flex-col gap-8 pt-4">
          <span className={STRONG}>{w.titleLabel}</span>
          <RadioTileGroup
            value={values.titles_in_invitations ? 'board' : 'neutral'}
            onValueChange={(next) =>
              setValues((prior) => ({ ...prior, titles_in_invitations: next === 'board' }))
            }
            aria-label={w.titleLabel}
            testId="board-calendar-settings-title"
          >
            <RadioTile
              value="neutral"
              label={w.titleNeutralValue(orgName)}
              description={w.titleNeutralDescription}
            />
            <RadioTile
              value="board"
              label={w.titleBoard}
              description={w.titleBoardDescription(exampleTitle)}
            />
          </RadioTileGroup>
        </div>

        <p className={BODY}>{w.appliesFromNow}</p>
        {save.isError ? (
          <ErrorMessage testId="board-calendar-settings-error">
            {failureLine(save.error)}
          </ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="board-calendar-settings-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            onClick={() => save.mutate()}
            testId="board-calendar-settings-save"
          >
            {w.save}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
